import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initGA, logPageView } from './analytics';
import { DarkModeProvider } from './DarkModeContext';
import Seo from './Seo';
import ScrollToTop from './ScrollToTop';
import ScrollToTopButton from './ScrollToTopButton';
import ChatLauncher from './ChatLauncher';
import Portfolio from './Portfolio';
import './App.css';

const TechStack = lazy(() => import('./TechStack'));
const ProjectsPage = lazy(() => import('./ProjectsPage'));
const ProjectDetail = lazy(() => import('./ProjectDetail'));
const Certifications = lazy(() => import('./Certifications'));
const SocialLinks = lazy(() => import('./SocialLinks'));
const Discussion = lazy(() => import('./Discussion'));
const Admin = lazy(() => import('./Admin'));
const Pay = lazy(() => import('./pay/PayApp'));
const VercelMetrics = lazy(() => import('./VercelMetrics'));

// Vercel Analytics + Speed Insights wait until the page has loaded and the browser is idle, so they never
// compete with the first paint (Speed Insights still reads the buffered LCP/CLS entries).
function LateMetrics() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setTimeout(() => setReady(true), 1500);
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }, []);
  return ready ? <Suspense fallback={null}><VercelMetrics /></Suspense> : null;
}

// Component to track page views
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    logPageView();
  }, [location]);

  return null;
}

// Everything inside the router. scripts/prerender.js renders this same tree with a StaticRouter,
// so each page ships as real HTML and the browser hydrates it.
export function AppRoutes() {
  return (
    <DarkModeProvider>
      <AnalyticsTracker />
      <Seo />
      <ScrollToTop/>
      <main>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Portfolio />} />
            <Route path="/tech-stack" element={<TechStack />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:filter" element={<ProjectsPage />} />
            <Route path="/project/:slug" element={<ProjectDetail />} />
            <Route path="/recent-projects" element={<Navigate to="/projects" replace />} />
            <Route path="/certifications" element={<Certifications />} />
            <Route path="/social-links" element={<SocialLinks />} />
            <Route path="/community" element={<Discussion />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/pay" element={<Pay />} />
          </Routes>
        </Suspense>
      </main>
      <ScrollToTopButton />
      <ChatLauncher />
      <LateMetrics />
    </DarkModeProvider>
  );
}

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
